<template>
  <b-container class="d-flex justify-content-center align-items-center wrapper">
    <b-row class="text-center mb-3">
      <b-col cols="12" class="mb-5">
        <img :src="getImage(brandingConfig.logos.logo)"
             class="img-fluid-branded-logo"
             alt=""
             loading="lazy"
        />
      </b-col>

      <b-col cols="12">
        {{ $t('global.redirect.header') }}
      </b-col>

      <b-col cols="12" class="mb-4">
        <!-- Text interpolation is always HTML-escaped, so displaying the raw (unvalidated)
             query param here is safe - only the :href binding below needs to use the
             sanitized `path`, which is the actual navigation sink. -->
        <SLink data-test="redirect-target-link" :href="path || '#'">
          {{ $route.query.path }}
        </SLink>
      </b-col>

      <b-col cols="12">
        <SLink v-if="path"
               data-test="redirect-follow-button"
               appearance="b-button"
               href="#"
               variant="primary"
               open-externally
               @click="manualRedirect"
        >
          {{ $t('global.redirect.follow') }}
        </SLink>

        <b-button v-else
                  data-test="redirect-follow-button"
                  variant="primary"
                  @click="manualRedirect"
        >
          {{ $t('global.redirect.follow') }}
        </b-button>
      </b-col>

      <b-col cols="12">
        <b-button variant="link" @click="$router.push($defaultRoute())">
          {{ $t('global.cancel') }}
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import brandingMixin from 'src/mixins/branding/brandingMixin.js'

// Only office.sdui.app (WOPI collaborative editing) is ever a legitimate target for this
// route - it is only ever reached via SduiListFileItem's submitWopiForm(). Fails closed:
// if the env var is ever missing, nothing validates and every URL is rejected.
const WOPI_ALLOWED_HOSTS = [import.meta.env.VITE_WOPI_EDIT_HOST].filter(Boolean)

// Guards against DOM XSS (e.g. `javascript:` URLs) and open redirects: only an absolute
// https URL to an allowed host is accepted, everything else is rejected.
const toSafeHttpsUrl = (value, allowedHosts) => {
  if (typeof value !== 'string' || !value) return null
  try {
    const url = new URL(value)
    if (url.protocol !== 'https:') return null
    if (!allowedHosts.includes(url.hostname)) return null
    return url.href
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return null
  }
}

export default {
  name: 'Redirect',
  mixins: [brandingMixin],
  computed: {
    path() {
      return toSafeHttpsUrl(this.$route.query.path, WOPI_ALLOWED_HOSTS)
    },

    formData() {
      try {
        return JSON.parse(this.$route.query?.data ?? 'null')
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        return null
      }
    },

    editUri() {
      return toSafeHttpsUrl(this.formData?.edit_uri, WOPI_ALLOWED_HOSTS)
    },

    hasForm() {
      return !!this.editUri
    },
  },

  methods: {
    manualRedirect() {
      if (this.path) {
        location.href = this.path
        return
      }

      this.$router.push(this.$defaultRoute())
    },

    submitForm() {
      if (!this.hasForm) return
      const { edit_access_token, ttl } = this.formData
      const HTMLForm = document.createElement('form')
      HTMLForm.setAttribute('id', 'wopiForm')
      HTMLForm.setAttribute('method', 'post')
      HTMLForm.setAttribute('action', this.editUri)
      HTMLForm.setAttribute('enctype', 'multipart/form-data')

      const iToken = document.createElement('input')
      iToken.setAttribute('name', 'access_token')
      iToken.setAttribute('value', edit_access_token)
      iToken.setAttribute('type', 'hidden')

      const iDate = document.createElement('input')
      iDate.setAttribute('name', 'ttl')
      iDate.setAttribute('value', ttl)
      iDate.setAttribute('type', 'hidden')

      const button = document.createElement('button')
      button.setAttribute('type', 'submit')

      HTMLForm.appendChild(iToken)
      HTMLForm.appendChild(iDate)
      HTMLForm.appendChild(button)
      document.getElementsByTagName('body')[0].appendChild(HTMLForm)
      HTMLForm.submit()
      document.getElementsByTagName('body')[0].removeChild(HTMLForm)
    },
  },

  created() {
    this.submitForm()
  },
}
</script>

<style scoped lang="scss">
.wrapper {
  height: 90vh;
}
</style>
